// Dialogo reusable para crear/editar un usuario de TRS Rastreo — extraido de
// TrsRastreoUsuariosPage.jsx (mismos campos y misma logica) para poder
// abrirlo tambien desde el arbol de ClientePlataformaPanel.jsx.
import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { crearUsuario, editarUsuario } from "../services/trsRastreoService";

const USU_TIPOS = [
  { value: 1, label: "Administrador" },
  { value: 2, label: "Inicio 1ra pestaña (Cliente)" },
  { value: 3, label: "Inicio 2da pestaña (Cliente)" },
];

const emptyForm = { usu_nombre: "", usu_username: "", usu_password: "", usu_email: "", usu_activo: true, usu_tipo: 1 };

export default function TrsUsuarioFormDialog({
  open,
  mode = "create", // "create" | "edit"
  usuario = null, // requerido cuando mode="edit"
  codServicioCliente, // requerido cuando mode="create"
  onClose,
  onSaved,
}) {
  const isEdit = mode === "edit";
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setError("");
    if (isEdit && usuario) {
      setForm({
        usu_nombre: usuario.usu_nombre || "",
        usu_username: usuario.usu_username || "",
        usu_password: "",
        usu_email: usuario.usu_email || "",
        usu_activo: usuario.usu_activo !== false,
        usu_tipo: usuario.usu_tipo || 1,
      });
    } else {
      setForm(emptyForm);
    }
  }, [open, isEdit, usuario]);

  const handleClose = () => {
    if (saving) return;
    onClose?.();
  };

  const handleSave = async () => {
    if (!form.usu_nombre.trim() || !form.usu_username.trim()) {
      setError("Nombre y usuario son obligatorios.");
      return;
    }
    if (!isEdit && !form.usu_password.trim()) {
      setError("La contraseña es obligatoria para un usuario nuevo.");
      return;
    }

    setSaving(true);
    setError("");
    try {
      if (isEdit) {
        const payload = {
          usu_nombre: form.usu_nombre,
          usu_username: form.usu_username,
          usu_email: form.usu_email,
          usu_activo: form.usu_activo,
          usu_tipo: form.usu_tipo,
          usu_cliente: usuario?.usu_cliente,
        };
        if (form.usu_password.trim()) payload.usu_password = form.usu_password;
        await editarUsuario(usuario.id, payload);
      } else {
        await crearUsuario({
          usu_nombre: form.usu_nombre,
          usu_username: form.usu_username,
          usu_password: form.usu_password,
          usu_email: form.usu_email,
          usu_cliente: codServicioCliente,
          usu_tipo: form.usu_tipo,
        });
      }
      onSaved?.();
    } catch (e) {
      setError(e?.response?.data?.error || e?.response?.data?.message || e.message || "No se pudo guardar el usuario");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: "1rem", fontWeight: 800 }}>
        {isEdit ? "Editar usuario" : "Adicionar usuario"}
        <IconButton size="small" onClick={handleClose}><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.25} sx={{ pt: 0.5 }}>
          {!!error && <Alert severity="error" sx={{ fontSize: "0.75rem" }}>{error}</Alert>}
          <TextField
            size="small"
            label="Nombre"
            value={form.usu_nombre}
            onChange={(e) => setForm((p) => ({ ...p, usu_nombre: e.target.value }))}
            disabled={saving}
          />
          <TextField
            size="small"
            label="Usuario"
            value={form.usu_username}
            onChange={(e) => setForm((p) => ({ ...p, usu_username: e.target.value }))}
            disabled={saving}
          />
          <TextField
            size="small"
            label={isEdit ? "Nueva contraseña" : "Contraseña"}
            helperText={isEdit ? "Dejar en blanco para no cambiarla" : ""}
            value={form.usu_password}
            onChange={(e) => setForm((p) => ({ ...p, usu_password: e.target.value }))}
            disabled={saving}
          />
          <TextField
            size="small"
            label="Email"
            value={form.usu_email}
            onChange={(e) => setForm((p) => ({ ...p, usu_email: e.target.value }))}
            disabled={saving}
          />
          <TextField
            select
            size="small"
            label="Tipo de usuario"
            value={form.usu_tipo}
            onChange={(e) => setForm((p) => ({ ...p, usu_tipo: Number(e.target.value) }))}
            disabled={saving}
          >
            {USU_TIPOS.map((t) => (
              <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
            ))}
          </TextField>
          {isEdit && (
            <FormControlLabel
              control={(
                <Checkbox
                  checked={form.usu_activo}
                  onChange={(e) => setForm((p) => ({ ...p, usu_activo: e.target.checked }))}
                  disabled={saving}
                />
              )}
              label="Activo"
            />
          )}
        </Stack>
      </DialogContent>
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, p: 1.5 }}>
        <Button size="small" onClick={handleClose} disabled={saving}>Cancelar</Button>
        <Button
          size="small"
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
        >
          Guardar
        </Button>
      </Box>
    </Dialog>
  );
}
